import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PRIORITIES } from "@/lib/helpers";
import { describeError } from "@/lib/api";
import { toast } from "sonner";

const blank = { title: "", description: "", due_date: "", due_time: "", priority: "medium", status: "todo", case_id: "", advocate_id: "", notes: "" };

export default function TaskDialog({ open, onOpenChange, initial, onSaved }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(blank);
  const [cases, setCases] = useState([]);
  const [advocates, setAdvocates] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing ? { ...blank, ...initial } : blank);
      api.get("/cases").then(({ data }) => setCases(data)).catch(() => {});
      api.get("/advocates").then(({ data }) => setAdvocates(data)).catch(() => {});
    }
  }, [open, initial, editing]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const save = async () => {
    if (!form.title) { toast.error("Task title is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, case_id: form.case_id || null, advocate_id: form.advocate_id || null };
      if (editing) await api.put(`/tasks/${initial.id}`, payload);
      else await api.post("/tasks", payload);
      toast.success(editing ? "Task updated" : "Task created");
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(describeError(err, "Could not save the task.")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-navy">{editing ? "Edit Task" : "Add Task"}</DialogTitle>
          <DialogDescription>Assign work and track it to completion.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Title *</Label><Input data-testid="task-title-input" value={form.title} onChange={set("title")} /></div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Description</Label><Textarea value={form.description} onChange={set("description")} rows={2} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Due Date</Label><Input type="date" data-testid="task-due-input" value={form.due_date || ""} onChange={set("due_date")} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Due Time</Label><Input type="time" value={form.due_time || ""} onChange={set("due_time")} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Priority</Label>
              <Select value={form.priority} onValueChange={set("priority")}><SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={form.status} onValueChange={set("status")}><SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todo">To Do</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent></Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Assign To</Label>
              <Select value={form.advocate_id || ""} onValueChange={set("advocate_id")}><SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Me" /></SelectTrigger>
                <SelectContent>{advocates.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Related Case</Label>
              <Select value={form.case_id || ""} onValueChange={set("case_id")}><SelectTrigger data-testid="task-case-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{cases.map((c) => <SelectItem key={c.id} value={c.id}>{c.case_number} — {c.title}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} data-testid="task-save-btn" className="bg-navy text-white hover:bg-navy-light">{saving ? "Saving…" : editing ? "Update Task" : "Add Task"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
